import React, { useState, useEffect } from 'react';
import { deusSocket, sendWithAuth } from '../utils/api';
import './MindSettingsModal.css'; // Custom CSS for styling
import LoadingSpinner from './LoadingSpinner';
import { useGlobalState } from '../context/GlobalState';


function MindSettingsModal({ isOpen, onClose, activeMind, setActiveProfile, activeProfile }) {
  const { minds, setMinds, user } = useGlobalState();
  const isPro = user?.membership === 'pro';

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [tempProfileText, setTempProfileText] = useState(null);
  const [saveTimer, setSaveTimer] = useState(null);
  const [saveStatus, setSaveStatus] = useState(''); // '' | 'saving' | 'saved' | 'error'
  const [statusTimer, setStatusTimer] = useState(null);
  const [saveField, setSaveField] = useState(''); // 'name' | 'description' | ''
  const [fineTuningStatus, setFineTuningStatus] = useState({}); // Track fine-tuning status for each profile
  const [showModelSelect, setShowModelSelect] = useState(null); // Store profile ID when showing model select
  const [selectedModel, setSelectedModel] = useState('gpt-4o-mini-2024-07-18'); // Default model
  const [deleteConfirmation, setDeleteConfirmation] = useState(null); // Store profile ID when showing delete confirmation
  const [showCreateOptions, setShowCreateOptions] = useState(false); // New state for showing create profile options
  // const [selectedTemplate, setSelectedTemplate] = useState('gpt v1'); // Default template
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [creatingProfile, setCreatingProfile] = useState(false);
  const [customProfileText, setCustomProfileText] = useState('');
  const [showCustomProfileInput, setShowCustomProfileInput] = useState(false);
  const [showInterviewOptions, setShowInterviewOptions] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isCallable, setIsCallable] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  
  // Simplified state for mind name and description
  const [tempMindName, setTempMindName] = useState('');
  const [tempDescription, setTempDescription] = useState('');
  const [nameErrorMessage, setNameErrorMessage] = useState('');
  // const [editingField, setEditingField] = useState(null); // 'name' | 'description' | null

  const AVAILABLE_MODELS = [
    { id: 'gpt-4o-2024-08-06', name: 'GPT-4 Optimized' },
    { id: 'gpt-4o-mini-2024-07-18', name: 'GPT-4 Mini (Recommended)' },
    { id: 'gpt-3.5-turbo-0125', name: 'GPT-3.5 Turbo' }
  ];

  const CREATION_METHODS = [
    { 
      id: 'custom', 
      name: 'Create Custom Character', 
      description: 'Write your own character description and let AI structure it into a complete profile.'
    },
    { 
      id: 'interview', 
      name: 'Generate from Interview', 
      description: 'Generate a profile based on your interview responses. Best for creating a digital version of yourself.',
      recommended: false
    },
    // { 
    //   id: 'random', 
    //   name: 'Generate Random Character', 
    //   description: 'Let AI create a unique and interesting character with a rich personality.'
    // }
  ];

  const INTERVIEW_TEMPLATES = [
    {
      id: 'deus v3',
      name: 'Deus v3',
      description: 'Template optimized for a concise profile identifying characteristics that would remain stable across contexts.'
    },
    {
      id: 'deus v2.5',
      name: 'Deus v2.5',
      description: 'Latest template optimized for natural conversation and personality accuracy.',
      recommended: true
    },
    // { 
    //   id: 'gpt v1', 
    //   name: 'Deus v2', 
    //   description: 'Template optimized for natural conversation and personality accuracy.',
    // },
    { 
      id: 'Deus v1',
      name: 'Deus v1',
      description: 'Original template focused on structured responses and clear personality traits.'
    },
    // {
    //   id: 'Character.ai',
    //   name: 'Character.ai',
    //   description: 'Character.ai style template with focus on roleplay and interactive storytelling.'
    // },
  ];

  // Get the active mind data from the minds array
  const activeMindData = minds.find(mind => mind.id === activeMind);

  // Set up listener for mind updates
  useEffect(() => {
    const handleMindUpdate = (updatedMind) => {
      // Only process updates for the active mind
      if (updatedMind.id === activeMind) {
        // Update the minds in global state by merging the updates
        // instead of replacing the whole object
        setMinds(prevMinds => prevMinds.map(mind => 
          mind.id === activeMind 
            ? { ...mind, ...updatedMind } // Merge instead of replace
            : mind
        ));
        
        // Show saved status
        setSaveStatus('saved');
        
        // Clear status after delay
        if (statusTimer) clearTimeout(statusTimer);
        const timer = setTimeout(() => {
          setSaveStatus('');
          setSaveField('');
        }, 2000);
        setStatusTimer(timer);
      }
    };
    
    // Listen for mind update events from the server
    deusSocket.on('deliverUpdateMind', handleMindUpdate);
    
    // Listen for error events
    const handleError = (error) => {
      if (saveStatus === 'saving') {
        setSaveStatus('error');
        
        // Clear error after delay
        if (statusTimer) clearTimeout(statusTimer);
        const timer = setTimeout(() => {
          setSaveStatus('');
          setSaveField('');
        }, 3000);
        setStatusTimer(timer);
      }
    };
    
    deusSocket.on('error-event', handleError);
    
    // Clean up listeners on unmount
    return () => {
      deusSocket.off('deliverUpdateMind', handleMindUpdate);
      deusSocket.off('error-event', handleError);
    };
  }, [activeMind, statusTimer, saveStatus]);
  
  // Set initial values when active mind changes
  useEffect(() => {
    if (activeMindData) {
      setIsCallable(activeMindData.is_callable || false);
      setIsPublic(activeMindData.is_public || false);
      setTempMindName(activeMindData.name || '');
      setTempDescription(activeMindData.description || '');
    }
  }, [activeMindData]);

  // Function to copy integration code to clipboard
  const copyIntegrationCode = () => {
    if (!activeMindData?.unique_code) return;
    
    navigator.clipboard.writeText(activeMindData.unique_code)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy code:', err);
      });
  };

  // Function to toggle callable status
  const toggleCallable = () => {
    if (!activeMind) return;
    
    const newValue = !isCallable;
    setIsCallable(newValue);
    
    // Send update to server
    sendWithAuth('updateMind', {
      mindId: activeMind,
      isCallable: newValue
    }, (response) => {
      if (response.success) {
        // Update minds in global state
        setMinds(prevMinds => prevMinds.map(mind => 
          mind.id === activeMind ? { ...mind, is_callable: newValue } : mind
        ));
      } else {
        // Revert the toggle if update failed
        setIsCallable(!newValue);
        console.error('Failed to update callable status:', response.error);
      }
    });
  };

  // Function to toggle public status
  const togglePublic = () => {
    if (!activeMind) return;
    
    const newValue = !isPublic;
    setIsPublic(newValue);
    
    // Send update to server
    sendWithAuth('updateMind', {
      mindId: activeMind,
      isPublic: newValue
    }, (response) => {
      if (response.success) {
        // Update minds in global state
        setMinds(prevMinds => prevMinds.map(mind => 
          mind.id === activeMind ? { ...mind, is_public: newValue } : mind
        ));
      } else {
        // Revert the toggle if update failed
        setIsPublic(!newValue);
        console.error('Failed to update public status:', response.error);
      }
    });
  };

  useEffect(() => {
    if (isOpen && activeMind) {
      setLoading(true);
      sendWithAuth('getCharacterProfiles', { mindId: activeMind }, (response) => {
        setLoading(false);
        if (response.success) {
          setProfiles(response.profiles);
          // Set the initial selectedProfile based on activeProfile
          const initialProfile = response.profiles.find(profile => profile.version === activeProfile);
          setSelectedProfile(initialProfile);
          if (initialProfile) {
            setTempProfileText(initialProfile.profile);
          }
        } else {
          console.error('Failed to retrieve profiles:', response.error);
        }
      });
    }
  }, [isOpen, activeMind, activeProfile]);

  // Listen for fine-tuning status updates
  useEffect(() => {
    const handleFineTuneStatus = (data) => {
      
      if (data.success) {
        // Immediately set the status
        setFineTuningStatus(prev => ({
          ...prev,
          [data.profileId]: {
            status: data.status,
            model: data.fineTunedModel
          }
        }));

        // If the fine-tuning succeeded, update the profile's model
        if (data.status === 'succeeded' && data.profile) {
          // Update the profiles list with the new model
          setProfiles(prevProfiles => 
            prevProfiles.map(p => 
              p._id === data.profile._id ? { ...p, model: data.fineTunedModel } : p
            )
          );
          
          // Set the status to 'completed' instead of deleting it
          // This allows the UI to properly transition
          setFineTuningStatus(prev => ({
            ...prev,
            [data.profileId]: {
              status: 'completed',
              model: data.fineTunedModel
            }
          }));
          
          // Show success notification
          alert('Fine-tuning completed successfully!');
          
          // After some time, clear the status to show the re-tune button
          setTimeout(() => {
            setFineTuningStatus(prev => {
              const newStatus = { ...prev };
              delete newStatus[data.profileId];
              return newStatus;
            });
          }, 3000);
        } else if (data.status === 'failed') {
          // Also handle the failed state explicitly
          setFineTuningStatus(prev => ({
            ...prev,
            [data.profileId]: { 
              status: 'failed',
              error: data.error || 'Unknown error'
            }
          }));
          
          // Show the error for a period before clearing
          setTimeout(() => {
            setFineTuningStatus(prev => {
              const newStatus = { ...prev };
              delete newStatus[data.profileId];
              return newStatus;
            });
          }, 3000);
          
          alert('Fine-tuning failed. Please try again.');
        }
      } else {
        setFineTuningStatus(prev => ({
          ...prev,
          [data.profileId]: {
            status: 'failed',
            error: data.error
          }
        }));
        
        // Clear failed status after showing error
        setTimeout(() => {
          setFineTuningStatus(prev => {
            const newStatus = { ...prev };
            delete newStatus[data.profileId];
            return newStatus;
          });
        }, 3000);
        
        if (data.error) {
          alert(`Fine-tuning failed: ${data.error}`);
        }
      }
    };

    deusSocket.on('fineTuneStatusUpdate', handleFineTuneStatus);
    return () => {
      deusSocket.off('fineTuneStatusUpdate', handleFineTuneStatus);
    };
  }, []);

  const handleFineTuneClick = (e, profile) => {
    e.stopPropagation();
    setShowModelSelect(profile._id);
    setSelectedModel('gpt-4o-mini-2024-07-18'); // Reset to default when opening
  };

  const handleFineTune = (profile) => {
    setShowModelSelect(null); // Hide model select
    // Set initial status
    setFineTuningStatus(prev => ({
      ...prev,
      [profile._id]: { status: 'starting' }
    }));

    sendWithAuth('fineTuneMind', {
      mindId: activeMind,
      profileId: profile._id,
      model: selectedModel
    }, (response) => {
      if (!response.success) {
        setFineTuningStatus(prev => ({
          ...prev,
          [profile._id]: { status: 'failed', error: response.error }
        }));
        alert(`Failed to start fine-tuning: ${response.error}`);
      }
    });
  };

  const handleSetActive = (profile) => {
    setActiveProfile(profile.version);
  };

  const handleSetDefault = (profile) => {
    const payload = {
      mindId: activeMind,
      profileId: profile._id
    }

    sendWithAuth('setDefaultProfile', payload, (response) => {
      if (response.success) {
        // alert('Profile set as default.');
        // Update profiles to reflect the new default
        setProfiles(profiles.map(p => p._id === profile._id ? { ...p, default_profile: true } : { ...p, default_profile: false }));
        // Update the minds global state with the new default profile version
        const updatedMinds = minds.map(mind => 
          mind.id === activeMind ? { ...mind, default_profile: profile.version } : mind
        );
        setMinds(updatedMinds);
      } else {
        console.error('Failed to set default profile:', response.error);
      }
    });
  };

  const handleProfileSelect = (profile) => {
    setSelectedProfile(profile);
    setTempProfileText(profile.profile);
  };

  const handleProfileChange = (e) => {
    const newValue = e.target.value;
    setTempProfileText(newValue);
    
    // Clear any existing timers
    if (saveTimer) clearTimeout(saveTimer);
    if (statusTimer) clearTimeout(statusTimer);
    
    
    // Set a new timer to save after 1 second of no typing
    const timer = setTimeout(() => {
      setSaveStatus('saving');
      if (selectedProfile && newValue !== selectedProfile.profile) {
        sendWithAuth('updateCharacterProfile', 
          { id: selectedProfile._id, profile: newValue }, 
          (response) => {
            if (response.success) {
              const updatedProfiles = profiles.map(p => 
                p._id === selectedProfile._id 
                  ? { ...p, profile: newValue }
                  : p
              );
              setProfiles(updatedProfiles);
              const updatedProfile = updatedProfiles.find(p => p._id === selectedProfile._id);
              setSelectedProfile(updatedProfile);
              setSaveStatus('saved');
              
              // Clear "Saved!" message after 2 seconds
              const newStatusTimer = setTimeout(() => {
                setSaveStatus('');
              }, 2000);
              setStatusTimer(newStatusTimer);
            } else {
              console.error('Failed to auto-save profile:', response.error);
              setSaveStatus('error');
              
              // Clear error message after 3 seconds
              const newStatusTimer = setTimeout(() => {
                setSaveStatus('');
              }, 3000);
              setStatusTimer(newStatusTimer);
            }
          }
        );
      }
    }, 1000);
    
    setSaveTimer(timer);
  };

  const handleDeleteClick = (e, profile) => {
    e.stopPropagation();
    setDeleteConfirmation(profile._id);
  };

  const handleDeleteConfirm = (e, profile) => {
    e.stopPropagation();
    
    // Check if profile is active or default
    if (profile.version === activeProfile) {
      alert('Cannot delete the active profile. Please set another profile as active first.');
      setDeleteConfirmation(null);
      return;
    }
    
    if (profile.default_profile) {
      alert('Cannot delete the default profile. Please set another profile as default first.');
      setDeleteConfirmation(null);
      return;
    }
    
    // Send delete request
    sendWithAuth('deleteCharacterProfile', 
      { id: profile._id, mindId: activeMind }, 
      (response) => {
        if (response.success) {
          // Remove the profile from the list
          setProfiles(profiles.filter(p => p._id !== profile._id));
          
          // If this was the selected profile, clear the selection
          if (selectedProfile && selectedProfile._id === profile._id) {
            setSelectedProfile(null);
            setTempProfileText(null);
          }
          
          setDeleteConfirmation(null);
        } else {
          console.error('Failed to delete profile:', response.error);
          alert(`Failed to delete profile: ${response.error}`);
          setDeleteConfirmation(null);
        }
      }
    );
  };

  const handleDeleteCancel = (e) => {
    e.stopPropagation();
    setDeleteConfirmation(null);
  };

  const handleCreateProfile = (optionType, template = null) => {
    if (optionType === 'interview' && !showInterviewOptions && !template) {
      setShowInterviewOptions(true);
      return;
    }

    if (optionType === 'custom' && !showCustomProfileInput) {
      setShowCustomProfileInput(true);
      return;
    }

    setCreatingProfile(true);
    setShowCreateOptions(false);
    setShowCustomProfileInput(false);
    setShowInterviewOptions(false);
    
    let payload = {
      mindId: activeMind,
      optionType: optionType === 'interview' ? template : optionType,
      automated: false,
    };

    if (optionType === 'custom') {
      payload.customDescription = customProfileText;
    }
    
    sendWithAuth('createCharacterProfile', payload, (response) => {
      setCreatingProfile(false);
      if (response.success) {
        // Add the new profile to the profiles list
        setProfiles(prevProfiles => [...prevProfiles, response.profile]);
        
        // Select the newly created profile
        setSelectedProfile(response.profile);
        setTempProfileText(response.profile.profile);
        setCustomProfileText(''); // Reset custom profile text
        
        // If this is the first profile or it's set as default, update the minds state
        if (response.profile.default_profile) {
          if (!activeProfile) {
            setActiveProfile(response.profile.version);
          }
          const updatedMinds = minds.map(mind => 
            mind.id === activeMind ? { ...mind, default_profile: 1 } : mind
          );
          setMinds(updatedMinds);
        }
      } else {
        console.error('Failed to create character profile:', response.error);
      }
    });
  };

  // Handle mind name change
  const handleMindNameChange = (e) => {
    setTempMindName(e.target.value);
    setNameErrorMessage('');
  };

  // Handle mind name blur (save when focus lost)
  const handleMindNameBlur = () => {
    if (!tempMindName.trim()) {
      setNameErrorMessage('Name cannot be empty');
      return;
    }
    
    const formattedName = tempMindName.trim();

    if (formattedName.includes(' ')) {
      setNameErrorMessage('Name cannot contain spaces');
      return;
    }
    
    if (formattedName !== activeMindData?.name) {
      // Set which field is being saved
      setSaveField('name');
      
      // Show saving status
      setSaveStatus('saving');
      
      // Send update to server (without callback)
      sendWithAuth('updateMind', {
        mindId: activeMind,
        name: formattedName
      });
    }
  };
  
  // Handle description change
  const handleDescriptionChange = (e) => {
    setTempDescription(e.target.value);
  };
  
  // Handle description blur (save when focus lost)
  const handleDescriptionBlur = () => {
    if (tempDescription !== activeMindData?.description) {
      // Set which field is being saved
      setSaveField('description');
      
      // Show saving status
      setSaveStatus('saving');
      
      // Send update to server (without callback)
      sendWithAuth('updateMind', {
        mindId: activeMind,
        description: tempDescription
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal show">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Mind Settings</h2>
          <button className="close-modal" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {/* Combined Mind Settings Section */}
          <div className="profile-item fixed-height-section mind-settings-section">
            <div className="profile-top-row">
              <div className="profile-info">
                <span className="version">Info</span>
              </div>
              {saveStatus && (
                <div className={`save-status ${saveStatus}`}>
                  {saveStatus === 'saving' && `Saving ${saveField}...`}
                  {saveStatus === 'saved' && `${saveField.charAt(0).toUpperCase() + saveField.slice(1)} saved!`}
                  {saveStatus === 'error' && `Failed to save ${saveField}!`}
                </div>
              )}
            </div>
            
            <div className="mind-settings-container">
              <div className="setting-group">
                <label htmlFor="mindName">Name</label>
                <input
                  id="mindName"
                  type="text"
                  value={tempMindName}
                  onChange={handleMindNameChange}
                  onBlur={handleMindNameBlur}
                  placeholder="Enter mind name"
                  className={nameErrorMessage ? 'input-error' : ''}
                />
                {nameErrorMessage && (
                  <div className="name-error">{nameErrorMessage}</div>
                )}
              </div>
              
              <div className="setting-group">
                <label htmlFor="mindDescription">Description</label>
                <textarea
                  id="mindDescription"
                  value={tempDescription}
                  onChange={handleDescriptionChange}
                  onBlur={handleDescriptionBlur}
                  placeholder="Enter a description for this mind..."
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Mind Visibility Controls */}
          {profiles.length > 0 ? (
            <div className="profile-item fixed-height-section">
              <div className="profile-top-row">
                <div className="profile-info">
                  <span className="version">Visibility Settings</span>
                </div>
              </div>
              
              <div className="toggle-controls">
                <div className="toggle-row">
                  <span className="left-label">Private</span>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={isPublic} 
                      onChange={togglePublic}
                    />
                    <span className="slider round"></span>
                  </label>
                  <div className="right-label-container">
                    <span className="right-label">Public</span>
                    <span className="info-icon" title="Make your integration code available to the public or keep it private">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="16" x2="12" y2="12"></line>
                        <line x1="12" y1="8" x2="12" y2="8"></line>
                      </svg>
                    </span>
                  </div>
                </div>
                
                <div className="toggle-row">
                  <span className="left-label">Offline</span>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={isCallable} 
                      onChange={toggleCallable}
                    />
                    <span className="slider round"></span>
                  </label>
                  <div className="right-label-container">
                    <span className="right-label">Online</span>
                    <span className="info-icon" title="Toggle your mind integrations between online and offline">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="16" x2="12" y2="12"></line>
                        <line x1="12" y1="8" x2="12" y2="8"></line>
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="profile-item fixed-height-section">
              <div className="profile-top-row">
                <div className="profile-info">
                  <span className="version">Integration Requirements</span>
                </div>
              </div>
              <div className="integration-requirements">
                <p>Create a profile to enable visibility settings and generate an integration code.</p>
              </div>
            </div>
          )}

          {/* Integration Code Section - Only show when profiles exist and unique code exists */}
          {profiles.length > 0 && activeMindData?.unique_code && (
            <div className="profile-item fixed-height-section">
              <div className="profile-top-row">
                <div className="profile-info">
                  <span className="version">Integration Code</span>
                  <span className="info-icon" title="Use your integration code to add your agent to group chats">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12" y2="8"></line>
                    </svg>
                  </span>
                </div>
                <button 
                  className={`copy-message-button ${isCopied ? 'copy-success' : ''}`}
                  onClick={copyIntegrationCode}
                  title="Copy integration code"
                >
                  {isCopied ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  )}
                </button>
              </div>
              <div className="integration-code">
                <code>{activeMindData.unique_code}</code>
              </div>
            </div>
          )}

          {/* About Profiles section */}
          <div className={`profile-description ${isDescriptionExpanded ? 'expanded' : ''}`}>
            <div 
              className="description-toggle"
              onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
            >
              <span>About Profiles</span>
              <span className="toggle-icon">{isDescriptionExpanded ? '−' : '+'}</span>
            </div>
            <div className="description-content">
              <div className="description-inner">
                <p>
                  Profiles are your agent's DNA, shaping how they think and respond. Each profile contains instructions and personality traits that make your agent unique.
                </p>
                <p>
                  Fine-tuning trains the AI model on your voice and responses from your interview. The more questions you answer the better your agent mirrors you.
                </p>
                <p>
                  Set a profile as <strong>Active</strong> to interact to test it locally in the chat, or make it the <strong>Default</strong> profile so it's used during public calls. You can edit the profile's instructions directly in the text editor below.
                </p>
              </div>
            </div>
          </div>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className="profiles-list">
                {/* Create Profile Button and Options */}
                {creatingProfile ? (
                  <div className="profile-item create-profile-item">
                    <LoadingSpinner />
                  </div>
                ) : !showCreateOptions ? (
                  <div 
                    className="profile-item create-profile-item create-profile-button"
                    onClick={() => setShowCreateOptions(true)}
                  >
                    Create New Profile
                  </div>
                ) : showInterviewOptions ? (
                  <div className="profile-item create-profile-item">
                    <div className="profile-top-row">
                      <div className="profile-info">
                        <span className="version">Select Interview Template</span>
                      </div>
                    </div>
                    <div className="creation-options">
                      {INTERVIEW_TEMPLATES.map(template => (
                        <div key={template.id} className="creation-option">
                          <div className="option-header">
                            <h3>{template.name}</h3>
                            {template.recommended && <span className="recommended-badge">Recommended</span>}
                          </div>
                          <p>{template.description}</p>
                          <button onClick={() => handleCreateProfile('interview', template.id)}>
                            Use Template
                          </button>
                        </div>
                      ))}
                      <button 
                        className="back-button"
                        onClick={() => {
                          setShowInterviewOptions(false);
                          setShowCreateOptions(true);
                        }}
                      >
                        ← Back to Creation Methods
                      </button>
                    </div>
                  </div>
                ) : showCustomProfileInput ? (
                  <div className="profile-item create-profile-item">
                    <div className="profile-top-row">
                      <div className="profile-info">
                        <span className="version">Create Custom Character</span>
                      </div>
                    </div>
                    <div className="profile-editor custom-profile-editor">
                      <div className="editor-instructions">
                        <p>Describe your character in detail. Consider including:</p>
                        <ul>
                          <li>Real people, living or dead</li>
                          <li>Background and personal history</li>
                          <li>Personality traits and behaviors</li>
                          <li>Beliefs, values, and worldview</li>
                          <li>Speaking style and mannerisms</li>
                          <li>Knowledge areas and expertise</li>
                          <li>Goals and motivations</li>
                        </ul>
                      </div>
                      <textarea
                        value={customProfileText}
                        onChange={(e) => setCustomProfileText(e.target.value)}
                        placeholder="Start describing your character..."
                        style={{ minHeight: '200px' }}
                      />
                      <div className="editor-actions">
                        <button 
                          className="back-button"
                          onClick={() => {
                            setShowCustomProfileInput(false);
                            setShowCreateOptions(true);
                          }}
                        >
                          ← Back
                        </button>
                        <button 
                          className="create-button"
                          onClick={() => handleCreateProfile('custom')}
                          disabled={!customProfileText.trim()}
                        >
                          Create Character
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="profile-item create-profile-item">
                    <div className="profile-top-row">
                      <div className="profile-info">
                        <span className="version">Choose Creation Method</span>
                      </div>
                    </div>
                    <div className="creation-options">
                      {CREATION_METHODS.map(method => (
                        <div key={method.id} className="creation-option">
                          <div className="option-header">
                            <h3>{method.name}</h3>
                            {method.recommended && <span className="recommended-badge">Recommended</span>}
                            {method.id === 'interview' && !isPro && (
                              <span className="pro-badge">Pro</span>
                            )}
                          </div>
                          <p>{method.description}</p>
                          <button 
                            onClick={() => handleCreateProfile(method.id)}
                            disabled={method.id === 'interview' && !isPro}
                            title={method.id === 'interview' && !isPro ? 'Upgrade to Pro to unlock interview creation' : ''}
                          >
                            {method.id === 'interview' && !isPro ? 'Pro Feature' : 'Select'}
                          </button>
                        </div>
                      ))}
                      <button 
                        className="back-button"
                        onClick={() => setShowCreateOptions(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                
                {profiles
                  .sort((a, b) => {
                    // Move active profile to top
                    if (a.version === activeProfile) return -1;
                    if (b.version === activeProfile) return 1;
                    // Secondary sort by version number (descending)
                    return b.version - a.version;
                  })
                  .map((profile) => (
                    <div 
                      key={profile.version} 
                      className={`profile-item ${profile.version === selectedProfile?.version ? 'active-profile' : ''}`}
                      onClick={() => handleProfileSelect(profile)}
                    >
                      <div className="profile-top-row">
                        <span className="version-template">
                          <span className="version">v{profile.version}</span>
                          {profile.template && <span className="template-value">{profile.template}</span>}
                        </span>
                        <button
                          className={`delete-profile ${deleteConfirmation === profile._id ? 'active' : ''}`}
                          onClick={(e) => handleDeleteClick(e, profile)}
                          disabled={profile.version === activeProfile || profile.default_profile || deleteConfirmation === profile._id}
                          title={
                            profile.version === activeProfile 
                              ? "Cannot delete active profile" 
                              : profile.default_profile 
                                ? "Cannot delete default profile" 
                                : deleteConfirmation === profile._id
                                  ? "Delete confirmation shown below"
                                  : "Delete profile"
                          }
                        >
                          🗑️
                        </button>
                      </div>
                      <div className="profile-actions-row">
                        <div className="profile-actions">
                          {profile.version === activeProfile ? (
                            <span className="status-label active-status">Active</span>
                          ) : (
                            <button 
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                handleSetActive(profile); 
                              }}
                            >
                              Set Active
                            </button>
                          )}
                          {profile.default_profile ? (
                            <span className="status-label default-status">Default</span>
                          ) : (
                            <button 
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                handleSetDefault(profile); 
                              }}
                            >
                              Make Default
                            </button>
                          )}
                        </div>
                        <div className="profile-actions">
                          {fineTuningStatus[profile._id]?.status === 'validating_files' ? (
                            <span className="status-label fine-tuning">Validating data...</span>
                          ) : fineTuningStatus[profile._id]?.status === 'starting' || 
                           fineTuningStatus[profile._id]?.status === 'running' ? (
                            <span className="status-label fine-tuning">Training...</span>
                          ) : fineTuningStatus[profile._id]?.status === 'succeeded' ? (
                            <span className="status-label fine-tuned">Fine-tuned</span>
                          ) : fineTuningStatus[profile._id]?.status === 'completed' ? (
                            <span className="status-label fine-tuned">Fine-tuned</span>
                          ) : fineTuningStatus[profile._id]?.status === 'failed' ? (
                            <span className="status-label fine-tuning-error">Failed</span>
                          ) : !isPro ? (
                            <button
                              className="fine-tune disabled"
                              title="Upgrade to Pro to unlock fine-tuning"
                              disabled={true}
                            >
                              Pro: Fine-tune
                            </button>
                          ) : profile.model ? (
                            <button
                              className={`fine-tune ${showModelSelect === profile._id ? 'disabled' : ''}`}
                              onClick={(e) => handleFineTuneClick(e, profile)}
                              disabled={showModelSelect === profile._id}
                            >
                              Re-tune
                            </button>
                          ) : (
                            <button
                              className={`fine-tune ${showModelSelect === profile._id ? 'disabled' : ''}`}
                              onClick={(e) => handleFineTuneClick(e, profile)}
                              disabled={showModelSelect === profile._id}
                            >
                              Fine-tune
                            </button>
                          )}
                        </div>
                      </div>
                      {showModelSelect === profile._id && (
                        <div className="fine-tune-action-row" onClick={e => e.stopPropagation()}>
                          <div className="fine-tune-controls">
                            <select 
                              value={selectedModel}
                              onChange={(e) => setSelectedModel(e.target.value)}
                              className="model-select"
                            >
                              {AVAILABLE_MODELS.map(model => (
                                <option key={model.id} value={model.id}>
                                  {model.name}
                                </option>
                              ))}
                            </select>
                            <div className="fine-tune-buttons">
                              <button
                                className="start-fine-tune"
                                onClick={() => handleFineTune(profile)}
                              >
                                Start Fine-tuning
                              </button>
                              <button
                                className="cancel-fine-tune"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowModelSelect(null);
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      {deleteConfirmation === profile._id && (
                        <div className="delete-confirmation-row" onClick={e => e.stopPropagation()}>
                          <div className="delete-confirmation">
                            <span>Are you sure you want to delete this profile? This action cannot be undone.</span>
                            <div className="delete-confirmation-buttons">
                              <button
                                className="confirm-delete"
                                onClick={(e) => handleDeleteConfirm(e, profile)}
                              >
                                Yes, Delete
                              </button>
                              <button
                                className="cancel-delete"
                                onClick={handleDeleteCancel}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      {profile.model && (
                        <div className="model-info-row">
                          <span className="model-label">Fine-tuned Model:</span>
                          <span className="model-badge">{profile.model}</span>
                        </div>
                      )}
                      {profile.version === selectedProfile?.version && (
                        <div className="profile-editor">
                          <textarea
                            value={tempProfileText || ''}
                            onChange={handleProfileChange}
                          />
                          {saveStatus && (
                            <div className={`save-status ${saveStatus}`}>
                              {saveStatus === 'saving' && 'Saving...'}
                              {saveStatus === 'saved' && 'Saved!'}
                              {saveStatus === 'error' && 'Save failed!'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MindSettingsModal;